import Templating from './Templating';
import Tracking from './Tracking';
/**
 * Creates an instance of PrediggoService, the object in charge of communicating
 * with Prediggo's server.
 *
 * @public
 * @class
 *
 * @param {string} serviceUrl The url of Prediggo's server.
 * @param {string} apiKey The API key.
 * @param {string} sessionId The session-id for the current user.
 * @param {number} pageId the API id of the current page.
 * @param {ConstrOptions} options Some optional configuration data.
 *
 */
class PrediggoService {
    constructor(serviceUrl, apiKey, sessionId, pageId, options) {
        this.version = '3.0.0';
        this.apiKey = apiKey;
        if (serviceUrl[serviceUrl.length - 1] !== '/') {
            serviceUrl = serviceUrl + '/';
        }
        if (options && options.sendApiKeyInUrl) {
            this.serviceUrl = serviceUrl + apiKey + '/2.0/';
        }
        else {
            this.serviceUrl = serviceUrl + 'api/2.0/';
        }
        this.verbose = options && options.verbose;
        this.pageId = pageId;
        this.tracking = new Tracking(this.verbose);
        this.prdSessionId = this.tracking.getIdentifier();
        if (sessionId) {
            this.sessionId = '' + sessionId;
        }
        else {
            this.sessionId = this.prdSessionId;
        }
        if (options && options.customerId) {
            this.customerId = options.customerId;
        }
        if (options && options.hasOwnProperty('followRedirects')) {
            this.followRedirects = options.followRedirects;
        }
        else {
            this.followRedirects = true;
        }
        if (options && options.languageCode) {
            this.languageCode = options.languageCode;
        }
        if (options && options.zoneName) {
            this.zoneName = options.zoneName;
        }
        if (options && options.timeout) {
            this.timeout = options.timeout;
        }
        if (options && options.region) {
            this.region = options.region;
        }
        if (options && options.offer) {
            this.offer = options.offer;
        }
        if (options && options.store) {
            this.store = options.store;
        }
        if (options && options.url) {
            this.url = options.url;
        }
        if (options && options.routing) {
            this.routing = options.routing;
        }
        if (options && Object.prototype.hasOwnProperty.call(options, "withHistory")) {
            this.withHistory = options.withHistory;
        }
        else {
            this.withHistory = true;
        }
        this.alreadySent = [];
        this._checkStoredNotif();
        this.callBackCounter = 0;
    }
    /**
     * Read the configuration file at /prediggoConfig.json.
     * If options are in localstorage, they will be used.
     * Example:
     * {
     *      "serviceUrl": "http://localhost:9001",
     *      "apiKey": "ERAMDEV",
     *      "sessionId": "test-session-XYZ",
     *
     *        "options": {
     *          "verbose": true,
     *          "followRedirects": false,
     *          "region": "FR_fr",
     *          "filters": {}
     *      }
     *  }
     *
     * Only used by the Vuejs demo
     *
     */
    static getConfig() {
        return fetch('/prediggoConfig.json')
            .then(response => response.json()
            .then(data => {
            const region = this.getRegion();
            if (region) {
                data.options.region = region;
            }
            const sessionId = this.getSessionId();
            if (sessionId) {
                data.sessionId = sessionId;
            }
            return data;
        }));
    }
    static getRegion() {
        return window.localStorage.getItem('prediggoRegion');
    }
    static setRegion(region) {
        window.localStorage.setItem('prediggoRegion', region);
    }
    static getSessionId() {
        return window.localStorage.getItem('prediggoSessionId');
    }
    static setSessionId(sessionId) {
        window.localStorage.setItem('prediggoSessionId', sessionId);
    }
    /**
     * Gets the value of the routing header.
     *
     * If the field 'routing' has been specified in the config,
     * we use that value. Else, we use the region if it is
     * set.
     */
    getRoutingHeader() {
        if (this.routing) {
            return this.routing;
        }
        if (this.region) {
            return this.region;
        }
        return undefined;
    }
    /**
     * Notifies the server of a click.
     *
     * @public
     *
     * @param {string} trackingCode the tracking code.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     * The callback success expects an object parameter with format
     *
     * {
     *  status: ...,
     *  timeInMs: ...,
     *  sessionId: ...,
     *  trackingCode: ...
     * }
     *
     */
    notify(trackingCode, success, error) {
        if (this.alreadySent.indexOf(trackingCode) > -1) {
            return;
        }
        this.tracking.storeNotif(trackingCode);
        var this_ = this;
        var onSuccess = function (data) {
            this_.tracking.removeNotif(trackingCode);
            if (success) {
                success(data);
            }
        };
        this._sendNotif(trackingCode, onSuccess, error);
    }
    /**
     * Sends a notification to Prediggo.
     *
     * @private
     *
     * @param {string} trackingCode the tracking code.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     */
    _sendNotif(trackingCode, success, error) {
        var queryTrack = {
            'sessionId': this.sessionId,
            'trackingCode': trackingCode
        };
        var page = 'track';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            this_.alreadySent.push(trackingCode);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in notify()');
            }
        };
        this._fetch(page, queryTrack, callSuccess, error);
    }
    /**
     *
     * Check in the cookies for queue of notifications.
     *
     * If any is found, sends the tracking codes to Prediggo.
     */
    _checkStoredNotif() {
        try {
            var queue = this.tracking.getNotifQueue();
            if (!queue) {
                return;
            }
            var this_ = this;
            for (var i = 0; i < queue.length; i++) {
                var trackingCode = queue[i];
                this_.log('Found stored tracking-code: ' + trackingCode);
                var success = function (data) {
                    this_.tracking.removeNotif(trackingCode);
                };
                var error = function (err) {
                    this_.log('Error in _checkStoredNotif(): ' + JSON.stringify(err));
                };
                this._sendNotif(trackingCode, success, error);
            }
        }
        catch (ex) {
            this.log('Exception in _checkStoredNotif: ' + ex);
        }
    }
    /**
     * Notifies the server the a user has logged in.
     *
     * (If called over a user already logged in, this will have no effect.)
     *
     * @param {string} customerId The identifier of the user's permanent
     * account (as opposed to a session id).
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     * The success callback should expect a response with format
     *
     * {
     *   status: ...,
     *   timeInMs: ...,
     *   sessionId: ...,
     *   customerId: ...
     * }
     *
     */
    registerUser(customerId, success, error) {
        var query = {
            'sessionId': this.sessionId,
            'customerId': customerId,
            'region': this.region
        };
        if (this.customerId === null || this.customerId === undefined) {
            this.customerId = customerId;
        }
        var page = 'registerUser';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in registerUser()');
            }
        };
        this._fetch(page, query, callSuccess, error);
    }
    /**
     * Fetches the information stored about a user.
     *
     * @param {string} customerId The user's customer id.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    viewUser(customerId, success, error) {
        var query = {
            'sessionId': this.sessionId,
            'customerId': customerId
        };
        if (this.customerId === null || this.customerId === undefined) {
            this.customerId = customerId;
        }
        var page = 'viewUser';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in viewUser()');
            }
        };
        this._fetch(page, query, callSuccess, error);
    }
    /**
     * Deletes the information stored about a user.
     *
     * @param {string} customerId The user's customer id.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    deleteUser(customerId, success, error) {
        var query = {
            'sessionId': this.sessionId,
            'customerId': customerId
        };
        if (this.customerId === null || this.customerId === undefined) {
            this.customerId = customerId;
        }
        var page = 'deleteUser';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in deleteUser()');
            }
        };
        this._fetch(page, query, callSuccess, error);
    }
    /**
     * Fetches a list of all pages available with their blocks.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    getListPages(success, error) {
        var queryListPages = {};
        var page = 'listPages';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            var pages = data.pages;
            if (success) {
                success(pages);
            }
            else {
                this.log('No success callback in getListPages()');
            }
        };
        this._fetch(page, queryListPages, callSuccess, error);
    }
    /**
     * Fetches recommendations to populate a single block.
     *
     * @param {string} itemId the item-id of the current product.
     * @param {number} blockId The API number of the block.
     * @param {Options} options An optional object containing further settings.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     *
     */
    getRecommendationBlock(itemId, blockId, success, error, options) {
        var advanced = this._getAdvanced(options);
        var pageContent = new PageContent(advanced);
        pageContent.addRecommendationBlock([itemId], blockId, options);
        this.getPageContent(pageContent, success, error);
    }
    /**
     * Fetches search results to populate a single search block.
     *
     * @param {string} query The search query.
     * @param {number} blockId The API number of the block.
     * @param {Options} options An optional object containing further settings.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    getSearchBlock(query, blockId, success, error, options) {
        var advanced = this._getAdvanced(options);
        var pageContent = new PageContent(advanced);
        pageContent.addSearchBlock(query, blockId, options);
        this.getPageContent(pageContent, success, error);
    }
    /**
     * Fetches recommendations for a single block when no query and no item-id are available.
     *
     * @param {number} blockId The API number of the block.
     * @param {Options} options An optional object containing further settings.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    getGenericBlock(blockId, success, error, options) {
        var advanced = this._getAdvanced(options);
        var pageContent = new PageContent(advanced);
        pageContent.addRecommendationBlock(null, blockId, options);
        this.getPageContent(pageContent, success, error);
    }
    /**
     * Fetches recommendations for a single block based on a list of itemIds
     * (useful for a basket page).
     *
     * @param {string[]} itemIds An array of item-ids.
     * @param {number} blockId The API number of the block.
     * @param {Options} options An optional object containing further settings.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    getBasketBlock(itemIds, blockId, success, error, options) {
        var advanced = this._getAdvanced(options);
        var pageContent = new PageContent(advanced);
        pageContent.addRecommendationBlock(itemIds, blockId, options);
        this.getPageContent(pageContent, success, error);
    }
    /**
     * Fetches recommendations for a block on a navigation page.
     *
     * This will fetch recommendations for a block of type 'CATEGORY'.
     * The method has two signatures, either with an attribute-name and
     * an attribute-value followed by the block-id, or with a filter object
     * followed by the block-id.
     *
     * The first case is for simple situations where we only need to find
     * products with a given value in an attribute. The second covers more
     * complicated cases with multiple attributes and values.
     *
     * This is a short-cut method based on SimplePageContent.
     *
     * @param {String} attributeName The name of the category-attribute.
     * @param {String} attributeValue The value of the category-attribute.
     * @param {number} blockId The id of the block.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     *
     * or
     *
     * @param {String} filters A filter object.
     * @param {number} blockId The id of the block.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     *
     */
    getCategoryBlock(first, second, third, fourth, fifth, sixth) {
        if (typeof first === 'string' || first instanceof String) {
            // We have a pair of attribute name/value as first and second arguments
            this._getCategBlockPair(first, second, third, fourth, fifth, sixth);
        }
        else if (typeof first === 'object') {
            // We have a filter object as first argument
            this._getCategBlockFilter(first, second, third, fourth, fifth);
        }
    }
    /**
     * Fetches recommendations for a block on a navigation page given a pair
     * of attribute name and value.
     *
     * @private
     *
     * @param {String} attributeName The name of the category-attribute.
     * @param {String} attributeValue The value of the category-attribute.
     * @param {number} blockId The id of the block.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     */
    _getCategBlockPair(attributeName, attributeValue, blockId, success, error, options) {
        if (!options) {
            options = {};
        }
        if (!options.filters) {
            options.filters = {};
        }
        options.filters[attributeName] = [attributeValue];
        var advanced = this._getAdvanced(options);
        var pageContent = new PageContent(advanced);
        pageContent.addSearchBlock('', blockId, options);
        this.getPageContent(pageContent, success, error);
    }
    /**
     * Fetches recommendations for a block on a navigation page given an
     * object with filters.
     *
     * @private
     *
     * @param {String} filters A filter object.
     * @param {number} blockId The id of the block.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     */
    _getCategBlockFilter(filters, blockId, success, error, options) {
        if (!options) {
            options = {};
        }
        options.filters = filters;
        var advanced = this._getAdvanced(options);
        var pageContent = new PageContent(advanced);
        pageContent.addSearchBlock('', blockId, options);
        this.getPageContent(pageContent, success, error);
    }
    /**
     * Fetches a complex page-content response given a pageContent object.
     *
     * @param {string} query The search query.
     * @param {number} blockId The API id of the block.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     */
    getPageContent(pageContent, success, error) {
        if (!pageContent) {
            if (this.verbose) {
                console.log('No pageContent arg in getPageContent()');
            }
            return;
        }
        var query = {
            'sessionId': this.sessionId,
            'languageCode': this.languageCode,
            'pageId': this.pageId,
            'advanced': this._getAdvanced(pageContent.advanced),
            'zoneName': this.zoneName,
            'region': this.region,
            'blocks': {
                'searches': pageContent.searchBlocks,
                'recommendations': pageContent.recoBlocks
            }
        };
        var page = 'pageContent';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            this_._followRedirects(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in getPageContent()');
            }
        };
        this._fetch(page, query, callSuccess, error);
    }
    /**
     * Fetches the results to an autocomplete query.
     *
     * @param {string} query The query.
     * @param {Options} options An optional object containing further settings.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    autoComplete(query, success, error, options) {
        var filters = undefined;
        if (options && options.filters) {
            filters = options.filters;
        }
        var advanced = this._getAdvanced(options);
        var queryAuto = {
            'sessionId': this.sessionId,
            'languageCode': this.languageCode,
            'query': query,
            'advanced': advanced,
            'region': this.region,
            'filters': filters
        };
        var page = 'autoComplete';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in autoComplete()');
            }
        };
        this._fetch(page, queryAuto, callSuccess, error);
    }
    /**
     * Notifies Prediggo that a user has interacted with an object.
     *
     * <p>
     *  The eventType is one of the following strings:
     *
     * <ul>
     *   <li>WEBSITE_ITEM_VIEWED                - An item has been visualized (not necessarily an item returned by Prediggo)</li>
     *   <li>WEBSITE_ITEM_WISHLISTED            - An item has been added to the customer's wish list (not necessarily an item returned by Prediggo)</li>
     *   <li>WEBSITE_REMOVE_ITEM_FROM_WISHLIST  - Item(s) to remove from the wishlist</li>
     *   <li>WEBSITE_ITEM_PURCHASED             - Item(s) have been purchased</li>
     *   <li>WEBSITE_ADD_ITEM_TO_BASKET         - Item(s) are added to the basket</li>
     *   <li>WEBSITE_REMOVE_ITEM_FROM_BASKET    - Item(s) to remove from the basket</li>
     *   <li>WEBSITE_SET_ITEMS_IN_BASKET        - Entirely reset or reload the basket content</li>
     * </ul>
     *
     * </p>
     *
     * <p>
     *   The itemIds, if mentioned, are a list of strings.
     * </p>
     *
     * <p>
     *   The baskets, if mentioned, are a list of the form:
     *   <blockquote>
     *      [{itemId: 'item-id-1', quantity: 1}, {itemId: 'item-id-2', quantity: 3}, ...]
     *   </blockquote>
     * </p>
     *
     *
     * @param {string} eventType The type of event to notify (see above).
     * @param {string[]} itemIds The ids of the products interacted with.
     * @param {Object[]} basket the baskets affected by the interaction, if any.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify advanced settings.
     *
     * The success callback should expect a response with format
     *
     * {
     *   status: ...,
     *   timeInMs: ...,
     *   sessionId: ...
     * }
     *
     */
    itemsInteraction(eventType, itemIds, basket, success, error, options) {
        if (!this._checkInteractionType(eventType)) {
            this.log('Unknown eventType in itemsInteraction: ' + eventType);
            return;
        }
        var advanced = this._getAdvanced(options);
        var query = {
            'sessionId': this.sessionId,
            'eventType': eventType,
            'advanced': advanced
        };
        if (itemIds !== undefined && itemIds !== null) {
            if (itemIds instanceof Array) {
                query['itemIds'] = itemIds;
            }
            else {
                query['itemIds'] = [itemIds];
            }
        }
        if (basket !== undefined && basket !== null) {
            query['basket'] = basket;
        }
        var page = 'itemsInteraction';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success function in itemsInteraction()');
            }
        };
        this._fetch(page, query, callSuccess, error);
    }
    /**
     * Refines a query in accordance with a refining-id.
     *
     * @param {string} refiningId the refining id.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Object} filters An object containing filters to constrain the products.
     *
     */
    refine(refiningId, success, error, filters = null) {
        if (this.withHistory) {
            if (!window || !window.location || !window.location.href) {
                this._sendRefineRequest(refiningId, success, error, filters);
            }
            var url = window.location.href;
            url = this._shapeNewUrl(url, refiningId);
            if (window.history && window.history.pushState) {
                window.history.pushState({}, '', url);
                this._sendRefineRequest(refiningId, success, error, filters);
            }
            else {
                window.location.href = url;
            }
        }
        else {
            this._sendRefineRequest(refiningId, success, error, filters);
        }
    }
    _insertParameter(url, key, value) {
        if (url.indexOf(key) > -1) {
            var reg = new RegExp(key + '=[^&]+', 'g');
            return url.replace(reg, key + '=' + value);
        }
        if (url.indexOf('?') >= 0) {
            return url + '&' + key + '=' + value;
        }
        return url + '?' + key + '=' + value;
    }
    _shapeNewUrl(url, refiningId) {
        if (refiningId.indexOf('=') < 0) {
            url = this._insertParameter(url, 'prd_refId', refiningId);
        }
        else {
            var splits = refiningId.split(/&/);
            for (var i = 0; i < splits.length; i++) {
                var split = splits[i];
                var key_value = split.split('=');
                if (key_value.length > 1) {
                    url = this._insertParameter(url, key_value[0], key_value[1]);
                }
            }
        }
        return url;
    }
    /**
     * Looks into the url of the current page for a refining-id and
     * sends a request for the corresponding content if it finds any.
     * The result will be passed to the callbacks.
     *
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @return {string} The refining-id if the url contains one, 'undefined'
     * if it containes none.
     */
    checkForRefiningUrl(success, error) {
        if (!window || !window.location || !window.location.href) {
            return undefined;
        }
        var url = window.location.href;
        var splits = url.split(/\?/);
        if (splits.length < 2) {
            return undefined;
        }
        /*
          The relevant parameters for us are

          prd_page
          prd_nbResultsPerPage
          prd_constraints
          prd_sorting
          prd_query
          prd_redirect
          prd_semanticSearchWeight

          prd_refId

        */
        var hasParam = /prd_refId|prd_page|prd_nbResultsPerPage|prd_constraints|prd_sorting|prd_query|prd_redirect|prd_semanticSearchWeight/.test(url);
        if (!hasParam) {
            return undefined;
        }
        var matches = url.match(/prd_refId=(\w+)/);
        if (matches && matches.length > 1) {
            var refiningId = matches[1];
            this._sendRefineRequest(refiningId, success, error);
            return refiningId;
        }
        var refiningId = splits[1];
        this._sendRefineRequest(refiningId, success, error);
        return refiningId;
    }
    /**
     * Fetches the results to refine a query.
     *
     * @param {string} refiningId the refining id.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {function} filters filters applied to the query
     */
    _sendRefineRequest(refiningId, success, error, filters = []) {
        var querySearch = {
            'sessionId': this.sessionId,
            'languageCode': this.languageCode,
            'pageId': this.pageId,
            'region': this.region,
            'parameters': {
                'zone': this.zoneName,
                'refiningId': refiningId,
                'filters': filters,
            },
            'advanced': { zone: this.zoneName }
        };
        var page = 'simplePageContent';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            this_._followRedirects(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in refine()');
            }
        };
        this._fetch(page, querySearch, callSuccess, error);
    }
    /**
     * Fetches the desired content for a SimplePageContent request
     *
     * @param {Object} simplePageContent A SimplePageContent request.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     */
    getSimplePageContent(simplePageContent, success, error) {
        var query = {
            'sessionId': this.sessionId,
            'languageCode': this.languageCode,
            'pageId': this.pageId,
            'region': this.region,
            'advanced': this._getAdvanced(simplePageContent.advanced),
            'parameters': simplePageContent.parameters
        };
        var page = 'simplePageContent';
        var this_ = this;
        var callSuccess = function (data) {
            this_._checkStatusTime(data);
            this_._followRedirects(data);
            if (success) {
                success(data);
            }
            else {
                this.log('No success callback in getSimplePageContent()');
            }
        };
        this._fetch(page, query, callSuccess, error);
    }
    /**
     * Fetches the content for a Home-page.
     *
     * This is a short-cut method based on SimplePageContent.
     *
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     *
     */
    getHomePage(success, error, options) {
        // For the homepage, no parameter is needed
        var simplePageContent = new SimplePageContent(null, null, options);
        this.getSimplePageContent(simplePageContent, success, error);
    }
    /**
     * Fetches the content for a product-page.
     *
     * This is a short-cut method based on SimplePageContent.
     *
     * @param {String} itemid The item-id of the main product of the page.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     *
     */
    getProductRecommendations(itemId, success, error, options) {
        // For the product page, we only need the item-id of the current product
        var simplePageContent = new SimplePageContent([itemId], null, options);
        this.getSimplePageContent(simplePageContent, success, error);
    }
    /**
     * Fetches recommendations for a backet-page.
     *
     * This is a short-cut method based on SimplePageContent.
     *
     * @param {String[]} itemIds The item-ids of the products in the basket.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Options} options An optional object to specify further settings.
     *
     */
    getBasketRecommendations(itemIds, success, error, options) {
        // For the basket page, we only need the itemIds
        var simplePageContent = new SimplePageContent(itemIds, null, options);
        this.getSimplePageContent(simplePageContent, success, error);
    }
    /**
     * Fetches the content for a navigation-page.
     *
     * Will fetch the content for a 'NAVIGATION'-type block. The method
     * can be called according to two signatures: either starting with
     * a pair attribute name / attribute value or a filters object.
     *
     * This is a short-cut method based on SimplePageContent.
     *
     * @param {String} attributeName The attribute that must constrain the products.
     * @param {String} attributeValue The value the products must have in the attribute.
     * @param {String} refiningId A refining-id provided by a previous call.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Object} options The search options.
     *
     * or
     *
     * @param {Object} filters An object containing filters to constrain the products.
     * @param {String} refiningId A refining-id provided by a previous call.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Object} options The search options.
     */
    getNavigationPage(first, second, third, fourth, fifth) {
        if (typeof first === 'string' || first instanceof String) {
            // We have a pair of attribute name/value as first and second arguments
            this._getNavPagePair(first, second, third, fourth, fifth);
        }
        else if (typeof first === 'object') {
            // We have a filter object as first argument
            this._getNavPageFilter(first, second, third, fourth);
        }
    }
    /**
     * Fetches the content for a navigation-page given a pair of attribute
     * name and value.
     *
     * @private
     *
     * @param {String} attributeName The attribute that must constrain the products.
     * @param {String} attributeValue The value the products must have in the attribute.
     * @param {String} refiningId A refining-id provided by a previous call.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Object} options The search options.
     */
    _getNavPagePair(attributeName, attributeValue, success, error, options) {
        if (!options) {
            options = {};
        }
        if (!options.filters) {
            options.filters = {};
        }
        options.filters[attributeName] = [attributeValue];
        // For navigation page  referenceSKUs, query, filters, refiningId, options, advanced
        var simplePageContent = new SimplePageContent(null, '', options);
        this.getSimplePageContent(simplePageContent, success, error);
    }
    /**
     * Fetches the content for a navigation-page given a pair of filters.
     *
     * @private
     *
     * @param {Object} filters An object containing filters to constrain the products.
     * @param {String} refiningId A refining-id provided by a previous call.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Object} options The search options.
     */
    _getNavPageFilter(filters, success, error, options) {
        if (!options) {
            options = {};
        }
        options.filters = filters;
        // For navigation page  referenceSKUs, query, filters, refiningId, options, advanced
        var simplePageContent = new SimplePageContent(null, '', options);
        this.getSimplePageContent(simplePageContent, success, error);
    }
    /**
     * Fetches the content for a search-page.
     *
     * This is a short-cut method based on SimplePageContent.
     *
     * @param {String} query A search query, i.e. a list of keywords.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     * @param {Object} options The search options.
     */
    getSearchPage(query, success, error, options) {
        // For search page  query, filters, refiningId, options
        var simplePageContent = new SimplePageContent(null, query, options);
        this.getSimplePageContent(simplePageContent, success, error);
    }
    /**
     * Extracts the Advanced settings from an option object.
     *
     * @private
     *
     * @param {Options} options The options passed by the caller.
     * @return {Object} the advanced settings mentioned in the parameter.
     *
     */
    _getAdvanced(advanced) {
        var ret = {
            zone: this.zoneName,
            customerId: this.customerId,
            store: this.store,
            offer: this.offer,
            url: this.url
        };
        if (advanced) {
            // If a customer-id has been specified in the options, we keep it
            if (advanced.customerId !== undefined && advanced.customerId !== null) {
                ret.customerId = advanced.customerId;
            }
            if (advanced.store !== undefined && advanced.store !== null) {
                ret.store = advanced.store;
            }
            if (advanced.offer !== undefined && advanced.offer !== null) {
                ret.offer = advanced.offer;
            }
            if (advanced.device !== undefined && advanced.device !== null) {
                ret.device = advanced.device;
            }
            if (advanced.referenceDate !== undefined && advanced.referenceDate !== null) {
                ret.referenceDate = advanced.referenceDate;
            }
            if (advanced.referer !== undefined && advanced.referer !== null) {
                ret.referer = advanced.referer;
            }
            if (advanced.zone !== undefined && advanced.zone !== null) {
                ret.zone = advanced.zone;
            }
            if (advanced.url !== undefined && advanced.url !== null) {
                ret.url = advanced.url;
            }
            if (advanced.segmentIdentifier !== undefined && advanced.segmentIdentifier !== null) {
                ret.segmentIdentifier = advanced.segmentIdentifier;
            }
        }
        return ret;
    }
    /**
     * Checks whether the data contain a block with a redirect
     * and follow it if the followRedirects flag is true (this is
     * the default case)
     *
     * @private
     *
     * @param {Object} data the data that may contain a redirect.
     */
    _followRedirects(data) {
        if (!this.followRedirects) {
            return; // We don't follow the redirects
        }
        if (!data.blocks || data.blocks.length < 1) {
            return; // No block, no redirect
        }
        if (!window || !window.location) {
            return;
        }
        var blocks = data.blocks;
        for (var name in blocks) {
            if (blocks.hasOwnProperty(name)) {
                var block = blocks[name];
                if (block.redirection && block.redirection.redirectUrl) {
                    window.location.replace(block.redirection.redirectUrl);
                    return;
                }
            }
        }
    }
    /**
     * Checks the status and time fields in the result of a call.
     *
     * @private
     *
     * @param {Object} data The JSON response to a call to Prediggo.
     *
     */
    _checkStatusTime(data) {
        if (this.verbose) {
            var status = data.status;
            var timeInMs = data.timeInMs;
            this.log('timeInMs: ' + timeInMs + ', status: ' + status);
        }
    }
    /**
     * Checks whether the type of iteraction is correct.
     *
     * @private
     *
     * @param {string} eventType A type of interaction.
     *
     * @return {boolean} whether the eventTyps is acceptable.
     */
    _checkInteractionType(eventType) {
        switch (eventType) {
            case 'WEBSITE_ITEM_VIEWED': return true;
            case 'WEBSITE_ITEM_WISHLISTED': return true;
            case 'WEBSITE_REMOVE_ITEM_FROM_WISHLIST': return true;
            case 'WEBSITE_ITEM_PURCHASED': return true;
            case 'WEBSITE_ADD_ITEM_TO_BASKET': return true;
            case 'WEBSITE_REMOVE_ITEM_FROM_BASKET': return true;
            case 'WEBSITE_SET_ITEMS_IN_BASKET': return true;
            default: return false;
        }
    }
    /**
     * Makes a call to a page on the base url.
     *
     * The method will first try to carry out an Ajax call. In
     * case of error, it will attempt to do the same call by JSONP.
     *
     * @private
     *
     * @param {string} page The entry point the call is addressed to.
     * @param {json} query The query to send.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     *
     */
    _fetch(page, query, success, error) {
        var url = this.serviceUrl + page;
        var this_ = this;
        if (!query) {
            query = {};
        }
        query.moduleVersion = 'js-' + this.version;
        query.prdSessionId = this.prdSessionId;
        var onAjaxError = function (msg) {
            this_._getJsonP(url, query, success, error);
            if (error) {
                error(msg);
                this_.log(msg);
            }
        };
        this._getAjax(url, query, success, onAjaxError);
    }
    /**
     * Returns an instance of XMLHttpRequest, or some version of
     * ActiveXObject in the older browsers.
     */
    _getXHTTP() {
        if (XMLHttpRequest) {
            return new XMLHttpRequest();
        }
        var versions = [
            'MSXML2.XmlHttp.6.0',
            'MSXML2.XmlHttp.5.0',
            'MSXML2.XmlHttp.4.0',
            'MSXML2.XmlHttp.3.0',
            'MSXML2.XmlHttp.2.0',
            'Microsoft.XmlHttp'
        ];
        for (var i = 0; i < versions.length; i++) {
            try {
                var xhttp = new ActiveXObject(versions[i]);
                return xhttp;
            }
            catch (ex) {
                // eslint-disable-line 
            }
        }
        return null;
    }
    /**
     * Makes an AJAX call to a url.
     *
     * @private
     *
     * @param {string} url The url of the entry point the call is addressed to.
     * @param {json} query The query to send.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     * We assume 'error' is always defined.
     *
     */
    _getAjax(url, query, success, error) {
        var xhttp = this._getXHTTP();
        if (!xhttp) {
            error('No xhttp.');
            return;
        }
        xhttp.open('POST', url, true);
        if (this.timeout) {
            xhttp.timeout = this.timeout;
        }
        xhttp.ontimeout = function (event) {
            error('Time out.');
        };
        xhttp.onreadystatechange = function (event) {
            try {
                if (xhttp.readyState == 4 && xhttp.status == 200) {
                    var json = JSON.parse(xhttp.responseText);
                    if (success) {
                        success(json);
                    }
                }
            }
            catch (ex) {
                error(JSON.stringify(ex));
            }
        };
        xhttp.onerror = function (event) {
            error(JSON.stringify(event));
        };
        xhttp.setRequestHeader('Content-type', 'application/json');
        xhttp.setRequestHeader('X-Prediggo-APIKey', this.apiKey);
        xhttp.setRequestHeader('X-Prediggo-Session', this.sessionId);
        var routing = this.getRoutingHeader();
        if (routing) {
            xhttp.setRequestHeader('X-Prediggo-Routing', routing);
        }
        xhttp.send(JSON.stringify(query));
    }
    /**
     * Makes a JSONP call to a url.
     *
     * @private
     *
     * @param {string} url The url of the entry point the call is addressed to.
     * @param {json} query The query to send.
     * @param {function} success The function called in case of success.
     * @param {function} error The function called in case of failure.
     *
     *
     */
    _getJsonP(url, query, success, error) {
        var callBackName = 'predcb_' + this._getCallBackCounter() + '_' + Math.floor(Math.random() * 1000000);
        var script = null;
        var head = null;
        try {
            head = document.getElementsByTagName('head')[0];
            window[callBackName] = function (payload) {
                if (success) {
                    success(payload);
                }
                if (window[callBackName]) {
                    window[callBackName] = undefined;
                }
                if (script && head) {
                    head.removeChild(script);
                }
            };
            script = document.createElement('script');
            script.onerror = function (event) {
                if (error) {
                    error(JSON.stringify(event));
                }
                if (window[callBackName]) {
                    window[callBackName] = undefined;
                }
                if (script && head) {
                    head.removeChild(script);
                }
            };
            script.language = 'javascript';
            script.type = 'text/javascript';
            script.src = url + '?callback=' + callBackName + '&query=' + encodeURIComponent(JSON.stringify(query));
            head.appendChild(script);
        }
        catch (ex) {
            if (error) {
                error(JSON.stringify(ex));
            }
            if (callBackName && window[callBackName]) {
                window[callBackName] = undefined;
            }
            if (script && head) {
                head.removeChild(script);
            }
        }
    }
    /**
     * Gets a new number to name the JSONP callback function.
     *
     * This avoids having several callbacks with the same name on the same page.
     *
     * @private
     *
     * @return {number} a number unique for the page for each call.
     *
     */
    _getCallBackCounter() {
        this.callBackCounter++;
        return this.callBackCounter;
    }
    log(msg) {
        if (this.verbose) {
            console.log('Prediggo logs: ' + msg);
        }
    }
}
/**
 * A class in charge of managing complex page-content requests, with multiple blocks of search- and recommendation- results.
 *
 * @class
 *
 * @param {Object} advanced An object specifying advanced settings (optional, may be empty or null).
 *
 */
class PageContent {
    constructor(advanced) {
        this.recoBlocks = [];
        this.searchBlocks = [];
        this.advanced = advanced;
    }
    /**
     * Adds a new recommendation block to the request.
     *
     * @param {string[]} itemIds The itemIds that serve as reference for the call.
     * @param {number} blockId The API id of the block.
     * @param {Option} options A configuration object containing additional settings.
     *
     */
    addRecommendationBlock(itemIds, blockId, options) {
        var filters = undefined;
        if (options && options.filters) {
            filters = options.filters;
        }
        var block = {
            'blockId': blockId,
            'skus': itemIds,
            'filters': filters
        };
        this.recoBlocks.push(block);
    }
    /**
     * Adds a new search block to the request.
     *
     * @param {string} query The search query.
     * @param {number} blockId The API id of the block.
     * @param {Option} options A configuration object containing additional settings.
     *
     */
    addSearchBlock(query, blockId, options) {
        var settings = this._getSearchSettings(options);
        var filters = undefined;
        if (options && options.filters) {
            filters = options.filters;
        }
        var block = {
            'blockId': blockId,
            'query': query,
            'filters': filters,
            'settings': settings
        };
        this.searchBlocks.push(block);
    }
    /**
     * Extracts the search setting from an option object.
     *
     * @private
     *
     * @param {Option} options A configuration object containing additional settings.
     *
     * @return {Object} The search settings as they are mentioned in the parameter.
     */
    _getSearchSettings(options) {
        var ret = {};
        if (options) {
            if (options.sortingCode) {
                ret.sortingCode = options.sortingCode;
            }
            if (options.resultsPerPage) {
                ret.resultsPerPage = options.resultsPerPage;
            }
            if (options.page) {
                ret.page = options.page;
            }
            ret.semanticSearchWeight = options.semanticSearchWeight;
        }
        return ret;
    }
}
/**
 * A class representing simple page-content requests.
 *
 * All the parameters are optional, depending on the content of the page.
 *
 * @class
 *
 * @param {String[]} itemIds A list of product ids.
 * @param {String} query A search query, i.e. a list of keywords.
 * @param {Object} filters An object of format {"key-1": [ values, ... ], "key-2": [ values, ... ], ...}
 * to restrict the products.
 * @param {Object} options The search options.
 * @param {Object} advanced The advanced settings.
 *
 */
class SimplePageContent {
    constructor(itemIds, query, options) {
        this.parameters = {};
        if (itemIds !== null && itemIds !== undefined) {
            this.parameters.skus = itemIds;
        }
        if (query !== null && query !== undefined) {
            this.parameters.query = query;
        }
        if (options) {
            this.parameters.sortingCode = options.sortingCode;
            this.parameters.resultsPerPage = options.resultsPerPage;
            this.parameters.page = options.page;
            this.parameters.semanticSearchWeight = options.semanticSearchWeight;
            this.parameters.filters = options.filters;
            this.advanced = {};
            this.advanced.customerId = options.customerId;
            this.advanced.device = options.device;
            this.advanced.referenceDate = options.referenceDate;
            this.advanced.referer = options.referer;
            this.advanced.zone = options.zone;
            this.advanced.offer = options.offer;
            this.advanced.store = options.store;
            this.advanced.url = options.url;
            this.advanced.segmentIdentifier = options.segmentIdentifier;
        }
    }
}
export { PrediggoService, Templating, Tracking, SimplePageContent, PageContent };
